import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import apiClient from "@/services/apiClient";

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebasestorage.app`,
  messagingSenderId: import.meta.env.VITE_FIREBASE_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let messagingInstance: firebase.messaging.Messaging | null = null;

// Debug logging
const log = (message: string, data?: unknown) => {
  console.log(`[FCM] ${message}`, data !== undefined ? data : "");
};

const logError = (message: string, error: unknown) => {
  console.error(`[FCM ERROR] ${message}`, error);
  if (error instanceof Error) {
    console.error(`[FCM ERROR] Name: ${error.name}`);
    console.error(`[FCM ERROR] Message: ${error.message}`);
    console.error(`[FCM ERROR] Stack: ${error.stack}`);
  }
};

/**
 * Check if Firebase is properly configured
 */
export function isFirebaseConfigured(): boolean {
  const configured = !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId &&
    VAPID_KEY
  );

  log("Configuration check:", {
    configured,
    apiKey: firebaseConfig.apiKey ? "✓ present" : "✗ missing",
    projectId: firebaseConfig.projectId ? "✓ present" : "✗ missing",
    messagingSenderId: firebaseConfig.messagingSenderId ? "✓ present" : "✗ missing",
    appId: firebaseConfig.appId ? "✓ present" : "✗ missing",
    vapidKey: VAPID_KEY ? `✓ present (${VAPID_KEY.substring(0, 20)}...)` : "✗ missing",
  });

  return configured;
}

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported(): boolean {
  const hasNotification = "Notification" in window;
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasPushManager = "PushManager" in window;

  log("Browser support check:", {
    Notification: hasNotification ? "✓" : "✗",
    serviceWorker: hasServiceWorker ? "✓" : "✗",
    PushManager: hasPushManager ? "✓" : "✗",
  });

  return hasNotification && hasServiceWorker && hasPushManager;
}

/**
 * Get the current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!("Notification" in window)) {
    return "denied";
  }
  return Notification.permission;
}

/**
 * Initialize Firebase and get the messaging instance
 */
function getMessagingInstance(): firebase.messaging.Messaging | null {
  if (!isFirebaseConfigured()) {
    log("Firebase not configured. Push notifications disabled.");
    return null;
  }

  if (!isPushSupported()) {
    log("Push notifications not supported in this browser.");
    return null;
  }

  if (messagingInstance) {
    log("Using existing messaging instance");
    return messagingInstance;
  }

  try {
    log("Initializing Firebase app (compat mode)...");
    log("Firebase config:", JSON.stringify(firebaseConfig, null, 2));

    // Initialize Firebase app if not already initialized
    if (firebase.apps.length === 0) {
      log("Creating new Firebase app");
      firebase.initializeApp(firebaseConfig);
    } else {
      log("Using existing Firebase app");
    }

    log("Getting messaging instance...");
    messagingInstance = firebase.messaging();
    log("Messaging instance created successfully (compat mode)");

    return messagingInstance;
  } catch (error) {
    logError("Failed to initialize Firebase messaging", error);
    return null;
  }
}

/**
 * Request push notification permission and register token with backend
 */
export async function requestPushPermission(): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    log("=== Starting push notification registration (COMPAT MODE) ===");

    // Step 1: Check if supported
    log("Step 1: Checking browser support...");
    if (!isPushSupported()) {
      return {
        success: false,
        error: "Push notifications not supported in this browser",
      };
    }
    log("Step 1: Browser support OK ✓");

    // Step 2: Check if configured
    log("Step 2: Checking Firebase configuration...");
    if (!isFirebaseConfigured()) {
      return {
        success: false,
        error: "Push notifications not configured",
      };
    }
    log("Step 2: Firebase configuration OK ✓");

    // Step 3: Request permission
    log("Step 3: Requesting notification permission...");
    log("Current permission status:", Notification.permission);
    const permission = await Notification.requestPermission();
    log("Permission result:", permission);
    if (permission !== "granted") {
      return {
        success: false,
        error: "Notification permission denied",
      };
    }
    log("Step 3: Permission granted ✓");

    // Step 4: Get messaging instance
    log("Step 4: Getting messaging instance...");
    const messaging = getMessagingInstance();
    if (!messaging) {
      return {
        success: false,
        error: "Failed to initialize messaging",
      };
    }
    log("Step 4: Messaging instance OK ✓");

    // Step 5: Register Service Worker and get token
    log("Step 5: Getting FCM token...");
    log("VAPID key being used:", VAPID_KEY);

    let token: string | null = null;

    try {
      // Register service worker first
      log("Registering service worker...");
      const swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      log("Service worker registered:", swRegistration.scope);

      // Wait for it to be ready
      await navigator.serviceWorker.ready;
      log("Service worker ready");

      // Use the compat API
      log("Calling messaging.getToken() with compat API...");
      token = await messaging.getToken({
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });

      log("Token obtained successfully!");
    } catch (tokenError) {
      logError("getToken failed", tokenError);

      // Try without service worker registration
      log("Trying getToken without explicit SW registration...");
      try {
        token = await messaging.getToken({ vapidKey: VAPID_KEY });
        log("Token obtained without SW registration!");
      } catch (error2) {
        logError("Second attempt also failed", error2);
        return {
          success: false,
          error: `Registration failed - ${(tokenError as Error).message}`,
        };
      }
    }

    if (!token) {
      log("No token obtained");
      return {
        success: false,
        error: "Failed to get push token",
      };
    }

    log("Step 5: Token obtained ✓");
    log("Token (first 50 chars):", token.substring(0, 50) + "...");

    // Step 6: Send token to backend
    log("Step 6: Sending token to backend...");
    try {
      await apiClient.post("/push-tokens", {
        token,
        platform: "web",
      });
      log("Step 6: Token sent to backend ✓");
    } catch (backendError) {
      logError("Failed to send token to backend", backendError);
      log("Warning: Token registered with FCM but failed to save to backend");
    }

    log("=== Push notification registration complete ===");
    return { success: true };
  } catch (error) {
    logError("Unexpected error during registration", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Unregister push notifications
 */
export async function unregisterPushNotifications(): Promise<void> {
  try {
    const messaging = getMessagingInstance();
    if (!messaging) return;

    const registration = await navigator.serviceWorker.getRegistration(
      "/firebase-messaging-sw.js"
    );

    if (registration) {
      const token = await messaging.getToken({
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        await apiClient.delete("/push-tokens", {
          data: { token },
        });
      }
    }
  } catch (error) {
    console.error("Failed to unregister push notifications:", error);
  }
}

/**
 * Set up listener for foreground messages
 */
export function onForegroundMessage(
  callback: (payload: { title?: string; body?: string; data?: Record<string, string> }) => void
): () => void {
  const messaging = getMessagingInstance();
  if (!messaging) {
    return () => { };
  }

  return messaging.onMessage((payload) => {
    console.log("Foreground message received:", payload);
    callback({
      title: payload.notification?.title,
      body: payload.notification?.body,
      data: payload.data,
    });
  });
}
